/**
 * Pure resolver for `handleOpenAssetDrawer`'s resolve-up logic.
 *
 * Lives outside `global.state.tsx` so it can be unit-tested without
 * pulling the whole GlobalStateProvider (and its test-setup module mock)
 * into the test.
 *
 * See rollup-frontend-plan.md item B.
 */
import type { PartialAsset } from '@/hooks/useAssets';
import { assetType } from '@/sections/asset/utils';

export interface HandleOpenAssetDrawerOptions {
  /**
   * When true (default), open the parent :Application drawer for member
   * observations whose `application.key` differs from their own `key`.
   * When false, open the asset as-is — used by drill-down call sites
   * inside the Application drawer's members section to prevent a loop.
   */
  resolveToGroup?: boolean;
}

/**
 * Given the asset passed to handleOpenAssetDrawer plus the resolveToGroup
 * option, return the resource we should actually open the drawer on.
 *
 * - Member with a resolved parent and `resolveToGroup: true` (default)
 *   → open the parent (drawer key = `asset.application.key`).
 * - Application row, orphan asset, or `resolveToGroup: false` → open the
 *   asset as-is.
 */
export function resolveAssetDrawerResource(
  asset: PartialAsset | undefined,
  options?: HandleOpenAssetDrawerOptions
): PartialAsset | undefined {
  if (!asset) return undefined;
  const resolveToGroup = options?.resolveToGroup ?? true;
  // An Application row is already the rollup root — never resolve it "up"
  // (it has no meaningful parent, and a self-referential `application.key`
  // would otherwise be a no-op spread we'd rather not perform). Members and
  // orphans fall through to the parent-key logic below.
  if (assetType(asset) === 'application') {
    return asset;
  }
  const parentKey = asset.application?.key;
  if (!resolveToGroup || !parentKey || parentKey === asset.key) {
    return asset;
  }
  return { ...asset, key: parentKey };
}

/**
 * The asset key a note/comment should be written to from the asset drawer.
 *
 * Notes always land on the parent :Application so the unified thread reads
 * identically from the parent drawer and any member drawer (Decision A):
 *  - member observation → `group_key` is the parent `#application#…` key.
 *  - parent :Application opened directly → `group_key` is empty, so we fall
 *    back to the asset's own key (which already IS the parent).
 *
 * Extracted as a pure function so `AssetDrawer.handleComment` and its test
 * exercise the SAME rule — a future change to the rule breaks the test
 * instead of silently diverging from a hand-copied expression.
 */
export function selectNoteTargetKey(asset: {
  key: string;
  group_key?: string | null;
}): string {
  return asset.group_key || asset.key;
}
